import { Filter, Search, Users } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { buildScoringTiers } from "../utils";

// Tier thresholds come from the recommended-threshold sheet (Audience column).
const scoringTiers = buildScoringTiers("AUDIENCE", [
	{
		key: "bestMatch",
		label: "Best Match",
		meaning: "The most accurate audience results.",
		warning: false,
	},
	{
		key: "strongMatch",
		label: "Strong Match",
		meaning: "Very relevant audience results.",
		warning: false,
	},
	{
		key: "acceptable",
		label: "Acceptable",
		meaning: "Results are generally relevant.",
		warning: false,
	},
	{
		key: "extended",
		label: "Extended",
		meaning: "Includes less relevant audience results.",
		warning: true,
	},
	{
		key: "maximumReach",
		label: "Maximum Reach",
		meaning: "Shows all qualifying audiences; many may have low relevance.",
		warning: true,
	},
]);

export const AudienceSearchInterface = ({
	// ✅ FIXED: Use the combined hook directly
	searchQuery,
	searchTerms,
	excludeTerms,
	isSearching,
	error,
	filters,

	// ✅ FIXED: Single search handler from combined hook
	onSearchInputChange,
	addSearchTerm,
	addExcludeTerm,
	removeSearchTerm,
	clearSearchTerms,
	triggerSearch, // ✅ Single search function from combined hook
	initialSortBy = "compositeScore",
	initialSortOrder = "DESC",
	onSortChange,
	className = "",
}) => {
	const [selectedTier, setSelectedTier] = useState(scoringTiers[1]); // Default to "Strong Match"

	// ✅ UPDATED: Sort state that will control the payload
	const [sortBy, setSortBy] = useState(initialSortBy);
	const [sortOrder, setSortOrder] = useState(initialSortOrder);

	const sortOptions = [
		{ value: "compositeScore", label: "Composite Score" },
		{ value: "soldImpressions", label: "Impressions" },
		{ value: "estimated_reach", label: "Estimated Reach" },
		{ value: "score", label: "Relevance score" },
		{ value: "clickThroughRate", label: "CTR" },
		{ value: "reach", label: "Reach" },
		{ value: "viewability", label: "Viewability" },
		{ value: "oxWinRate", label: "OX Win Rate" },
		{ value: "pubWinRate", label: "Pub Win Rate" },
		{ value: "bidCpm", label: "Bid CPM" },
		{ value: "bidGuidance", label: "Bid Guidance" },
	];

	// ✅ ADD: Sync local state when props change (for LLM results)
	useEffect(() => {
		console.log("📊 AudienceSearchInterface: Syncing sort from props:", {
			initialSortBy,
			initialSortOrder,
		});
		setSortBy(initialSortBy);
		setSortOrder(initialSortOrder);
	}, [initialSortBy, initialSortOrder]);

	// ✅ MODIFY: Notify parent when local sort changes
	const handleSortByChange = (e) => {
		const newSortBy = e.target.value;
		console.log("🔧 Search Interface: Sort by changed to:", newSortBy);
		setSortBy(newSortBy);
		if (onSortChange) {
			onSortChange(newSortBy, sortOrder);
		}
	};

	const handleSortOrderToggle = () => {
		const newOrder = sortOrder === "ASC" ? "DESC" : "ASC";
		console.log("🔧 Search Interface: Sort order changed to:", newOrder);
		setSortOrder(newOrder);
		if (onSortChange) {
			onSortChange(sortBy, newOrder);
		}
	};

	// ✅ TierDropdown component
	const TierDropdown = ({ selectedTier, onTierChange, scoringTiers }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		const handleTierSelect = (e) => {
			const tierLabel = e.target.value;
			const tier = scoringTiers.find((t) => t.label === tierLabel);
			onTierChange(tier);
		};

		return (
			<div className="tier-dropdown-container">
				<div className="tier-dropdown-wrapper">
					<select
						value={selectedTier.label}
						onChange={handleTierSelect}
						className="tier-simple-select"
					>
						{scoringTiers.map((tier) => (
							<option key={tier.label} value={tier.label}>
								{tier.label}
							</option>
						))}
					</select>

					<div
						className="info-icon-container"
						onMouseEnter={() => setShowTooltip(true)}
						onMouseLeave={() => setShowTooltip(false)}
					>
						<span className="info-icon">i</span>

						{showTooltip && (
							<div className="tier-tooltip">
								<div className="tooltip-header">
									<strong>Scoring Tiers</strong>
								</div>
								<div className="tooltip-content">
									{scoringTiers.map((tier, index) => (
										<div
											key={index}
											className={`tooltip-tier ${tier.warning ? "warning" : ""}`}
										>
											<div className="tooltip-tier-header">
												<span className="tooltip-tier-label">{tier.label}</span>
												<span className="tooltip-tier-score">
													{tier.score}+
												</span>
											</div>
											<div className="tooltip-tier-meaning">
												{tier.warning && (
													<span className="warning-indicator">⚠️ </span>
												)}
												{tier.meaning}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	const handleInputChange = useCallback(
		(e) => {
			const value = e.target.value;
			console.log("Input value changed:", value);

			// Call the hook's function with the string value
			if (typeof onSearchInputChange === "function") {
				onSearchInputChange(value);
			}
		},
		[onSearchInputChange],
	);

	// ✅ FIXED: Single add term handler
	// ✅ FIXED: Single add term handler with proper exclude handling
	const handleAddTerm = useCallback(
		(termType) => {
			// Safely convert searchQuery to string and trim
			const queryValue = String(searchQuery || "").trim();

			if (queryValue) {
				// The tier already carries the recommended cosine distance.
				const termData = {
					threshold: selectedTier.threshold,
					originalScore: selectedTier.score,
					type: termType,
				};

				console.log(`🏷️ Adding ${termType} term:`, queryValue, termData);

				// Call different functions based on term type
				if (termType === "exclude") {
					if (typeof addExcludeTerm === "function") {
						console.log("📤 Using addExcludeTerm function");
						addExcludeTerm(queryValue, termData);
					} else {
						console.log("📤 Using addSearchTerm with exclude flag");
						// Fallback: call addSearchTerm with explicit exclude flag as third parameter
						addSearchTerm(queryValue, termData, "exclude");
					}
				} else {
					console.log("📤 Using addSearchTerm for include");
					// Include term (default)
					addSearchTerm(queryValue, termData);
				}
			}
		},
		[searchQuery, selectedTier, addSearchTerm, addExcludeTerm],
	);

	// ✅ UPDATED: Pass sort values from interface to search function
	const handleSearchClick = useCallback(async () => {
		console.log("🔍 Search button clicked - triggering API call");
		console.log("📊 Current search terms:", [...searchTerms, ...excludeTerms]);
		console.log("🎯 Using sort from interface:", { sortBy, sortOrder });

		if (typeof triggerSearch === "function") {
			try {
				// ✅ Pass sort options from interface to override any filter-based sorting
				const searchOptions = {
					sortBy,
					sortOrder,
					overrideFilterSort: true, // Flag to indicate interface sort should take priority
				};

				await triggerSearch(searchOptions);
				console.log("✅ Search completed successfully");
			} catch (error) {
				console.error("❌ Search failed:", error);
			}
		} else {
			console.error("❌ No triggerSearch function available");
		}
	}, [triggerSearch, searchTerms, excludeTerms, sortBy, sortOrder]);

	const handleSearchKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleAddTerm("include");
			}
		},
		[handleAddTerm],
	);

	const handleTierChange = useCallback((tier) => {
		setSelectedTier(tier);
	}, []);

	const handleClearAll = useCallback(() => {
		clearSearchTerms();
	}, [clearSearchTerms]);

	// Combine include and exclude terms for display
	const allTerms = [
		...searchTerms.map((term) => ({ ...term, type: "include" })),
		...excludeTerms.map((term) => ({ ...term, type: "exclude" })),
	];

	const formatArrayDisplay = (array, maxLength = 60, maxItems = 3) => {
		if (!array || array.length === 0) return null;

		// If only one item, show it fully
		if (array.length === 1) {
			return array[0];
		}

		// Try to show as many items as possible within the character limit
		let result = array[0];
		let itemCount = 1;

		for (let i = 1; i < array.length && itemCount < maxItems; i++) {
			const nextItem = array[i];
			const testResult = result + ", " + nextItem;

			if (testResult.length <= maxLength) {
				result = testResult;
				itemCount++;
			} else {
				break;
			}
		}

		// If we couldn't fit all items, add count indicator
		if (itemCount < array.length) {
			const remaining = array.length - itemCount;
			result += ` (+${remaining} more)`;
		}

		return result;
	};

	// Helper function to format metric values
	const formatMetricValue = (value, type) => {
		if (!value || value === 0) return null;

		switch (type) {
			case "reach":
				return value >= 1000000
					? `${(value / 1000000).toFixed(1)}M`
					: value >= 1000
						? `${(value / 1000).toFixed(0)}K`
						: value.toString();
			case "currency":
				return value >= 1000000
					? `$${(value / 1000000).toFixed(1)}M`
					: value >= 1000
						? `$${(value / 1000).toFixed(0)}K`
						: `$${value}`;
			case "percentage":
				return `${value.toFixed(2)}%`;
			case "decimal":
				return value.toFixed(2);
			default:
				return value.toString();
		}
	};

	return (
		<div className={`audience-search-interface ${className}`}>
			{/* Search Terms Display */}
			{allTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span>Current search: </span>
							{allTerms.map((term, index) => (
								<span key={`${term.type}-${term.query}-${index}`}>
									<span
										className={`search-term ${term.type === "include" ? "term-include" : "term-exclude"}`}
									>
										{term.type === "include" ? "+" : "-"}
										{term.query} (
										{term.originalScore ||
											Math.round((term.threshold || 0) * 100)}
										)
										<button
											onClick={() => {
												console.log("🗑️ Removing term:", term);
												// Pass the term's query string, not the whole object
												removeSearchTerm(term.query);
											}}
											className="term-remove-btn"
										>
											×
										</button>
									</span>
									{index < allTerms.length - 1 && <span> </span>}
								</span>
							))}
						</div>
					</div>
					<button className="btn-clear-all-inline" onClick={clearSearchTerms}>
						Clear All
					</button>
				</div>
			)}

			{/* Main Search Row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<div className="search-icon-container">
						<Search size={16} />
					</div>
					<input
						type="text"
						placeholder="Search for audiences by name, demographics, or behavior..."
						value={String(searchQuery || "")}
						onChange={onSearchInputChange}
						onKeyPress={handleSearchKeyPress}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => handleAddTerm("include")}
							className="term-type-btn include-btn"
							disabled={!String(searchQuery || "").trim()}
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => handleAddTerm("exclude")}
							className="term-type-btn exclude-btn"
							disabled={!String(searchQuery || "").trim()}
						>
							Exclude
						</button>
					</div>
				</div>

				<div className="tier-input-container">
					<TierDropdown
						selectedTier={selectedTier}
						onTierChange={handleTierChange}
						scoringTiers={scoringTiers}
					/>
				</div>

				{/* ✅ FIXED: Single search button - only way to trigger API call */}
				<button
					type="button"
					onClick={handleSearchClick}
					className="search-button"
					disabled={isSearching}
				>
					{isSearching ? (
						<>
							<div className="loading-spinner-small"></div>
							Searching...
						</>
					) : (
						<>Search</>
					)}
				</button>
			</div>

			{/* ✅ UPDATED: Performance Filter Section with comprehensive sort options */}
			<div className="performance-filter-section">
				<div className="performance-filter-row">
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Sort by</label>
						</div>
						<div className="sort-controls">
							<select
								value={sortBy}
								onChange={handleSortByChange}
								className="sort-select"
							>
								{sortOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							<span
								onClick={handleSortOrderToggle}
								className="sort-order-arrow"
								title={sortOrder === "DESC" ? "Descending" : "Ascending"}
							>
								{sortOrder === "DESC" ? "↓" : "↑"}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/*Quick filter section*/}
			<div className="quick-filters">
				{/* ✅ UPDATED: Show current sort from interface */}
				<span className="quick-filter filter-basic sort-indicator">
					Sort:{" "}
					{sortOptions.find((opt) => opt.value === sortBy)?.label || sortBy} (
					{sortOrder === "DESC" ? "High to Low" : "Low to High"})
				</span>

				{/* Source and Provider Filters */}
				{filters.accountName?.length > 0 && (
					<span className="quick-filter filter-source">
						Accounts: {formatArrayDisplay(filters.accountName, 50)}
					</span>
				)}

				{filters.directAudienceProvider?.length > 0 && (
					<span className="quick-filter filter-source">
						Providers: {formatArrayDisplay(filters.directAudienceProvider, 50)}
					</span>
				)}

				{filters.dataSource?.length > 0 && (
					<span className="quick-filter filter-source">
						Data Source: {formatArrayDisplay(filters.dataSource, 40)}
					</span>
				)}

				{filters.region?.length > 0 && (
					<span className="quick-filter filter-source">
						Region: {formatArrayDisplay(filters.region, 40)}
					</span>
				)}

				{/* Audience Attributes */}
				{filters.shareable_openx_audience?.includes(true) && (
					<span className="quick-filter filter-attributes">
						Shareable OpenX: Yes
					</span>
				)}

				{filters.status?.length > 0 && (
					<span className="quick-filter filter-attributes">
						Status: {formatArrayDisplay(filters.status, 40)}
					</span>
				)}

				{filters.type?.length > 0 && (
					<span className="quick-filter filter-attributes">
						Type: {formatArrayDisplay(filters.type, 40)}
					</span>
				)}

				{filters.pricing?.length > 0 && (
					<span className="quick-filter filter-attributes">
						Pricing: {formatArrayDisplay(filters.pricing, 40)}
					</span>
				)}

				{/* Audience Descriptors */}
				{filters.entity?.length > 0 && (
					<span className="quick-filter filter-descriptors">
						Entity: {formatArrayDisplay(filters.entity, 40)}
					</span>
				)}

				{filters.event?.length > 0 && (
					<span className="quick-filter filter-descriptors">
						Event: {formatArrayDisplay(filters.event, 40)}
					</span>
				)}

				{filters.primaryPersona?.length > 0 && (
					<span className="quick-filter filter-descriptors">
						Primary Persona: {formatArrayDisplay(filters.primaryPersona, 40)}
					</span>
				)}

				{filters.audienceStrategy?.length > 0 && (
					<span className="quick-filter filter-descriptors">
						Strategy: {formatArrayDisplay(filters.audienceStrategy, 40)}
					</span>
				)}

				{filters.createdWith?.length > 0 && (
					<span className="quick-filter filter-descriptors">
						Created With: {formatArrayDisplay(filters.createdWith, 40)}
					</span>
				)}

				{/* Reach & Engagement Metrics */}
				{filters.reach > 0 && (
					<span className="quick-filter filter-metrics">
						Min Reach: {formatMetricValue(filters.reach, "reach")}
					</span>
				)}

				{filters.estimated_reach > 0 && (
					<span className="quick-filter filter-metrics">
						Min Est. Reach:{" "}
						{formatMetricValue(filters.estimated_reach, "reach")}
					</span>
				)}

				{filters.addressable_reach > 0 && (
					<span className="quick-filter filter-metrics">
						Min Addr. Reach:{" "}
						{formatMetricValue(filters.addressable_reach, "reach")}
					</span>
				)}

				{filters.Impressions > 0 && (
					<span className="quick-filter filter-metrics">
						Min Impressions: {formatMetricValue(filters.Impressions, "reach")}
					</span>
				)}

				{filters.Clicks > 0 && (
					<span className="quick-filter filter-metrics">
						Min Clicks: {formatMetricValue(filters.Clicks, "reach")}
					</span>
				)}

				{filters.ClickThroughRate > 0 && (
					<span className="quick-filter filter-metrics">
						Min CTR: {formatMetricValue(filters.ClickThroughRate, "percentage")}
					</span>
				)}

				{filters.VTR > 0 && (
					<span className="quick-filter filter-metrics">
						Min VTR: {formatMetricValue(filters.VTR, "percentage")}
					</span>
				)}

				{filters.Viewability > 0 && (
					<span className="quick-filter filter-metrics">
						Min Viewability:{" "}
						{formatMetricValue(filters.Viewability, "percentage")}
					</span>
				)}

				{/* Financial Metrics */}
				{filters.CPRMarketDemandPartnerSpendInUSD > 0 && (
					<span className="quick-filter filter-financial">
						Min Spend:{" "}
						{formatMetricValue(
							filters.CPRMarketDemandPartnerSpendInUSD,
							"currency",
						)}
					</span>
				)}

				{filters.CPRMarketPublisherRevenueInUSD > 0 && (
					<span className="quick-filter filter-financial">
						Min Revenue:{" "}
						{formatMetricValue(
							filters.CPRMarketPublisherRevenueInUSD,
							"currency",
						)}
					</span>
				)}

				{filters.bid_cpm > 0 && (
					<span className="quick-filter filter-financial">
						Min Bid CPM: {formatMetricValue(filters.bid_cpm, "decimal")}
					</span>
				)}

				{filters.Bid_Guidance > 0 && (
					<span className="quick-filter filter-financial">
						Min Bid Guidance:{" "}
						{formatMetricValue(filters.Bid_Guidance, "decimal")}
					</span>
				)}

				{/* Win Rate Metrics */}
				{filters.ox_win_rate > 0 && (
					<span className="quick-filter filter-winrate">
						Min OX Win Rate:{" "}
						{formatMetricValue(filters.ox_win_rate, "percentage")}
					</span>
				)}

				{filters.pub_win_rate > 0 && (
					<span className="quick-filter filter-winrate">
						Min Pub Win Rate:{" "}
						{formatMetricValue(filters.pub_win_rate, "percentage")}
					</span>
				)}
			</div>

			{/* Error Display */}
			{error && (
				<div className="error-display">
					<strong>Search Error:</strong> {error}
				</div>
			)}

			<style jsx>{`
      .quick-filters {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    padding-top: 12px;
                    border-top: 1px solid #e5e7eb;
                    align-items: center;
                }

                .quick-filter {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 8px;
                    background: #f3f4f6;
                    color: #6b7280;
                    border-radius: 4px;
                    font-size: 11px;
                    border: 1px solid #e5e7eb;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                /* ✅ Special styling for sort indicator */
                .sort-indicator {
                    background: #f3f4f6;
                    color: #6b7280;
                    border-color: #e5e7eb;
                    font-weight: 600;
                }
        .audience-search-interface {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          box-sizing: border-box;
        }

        .search-terms-display {
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          min-height: 36px;
          max-height: 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          gap: 12px;
        }

        .search-terms-content {
          flex: 1;
          overflow: hidden;
        }

        .current-search-term {
          font-size: 13px;
          color: #374151;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .search-term {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          margin: 2px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
        }

        .term-include {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .term-exclude {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .term-remove-btn {
          margin-left: 4px;
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-weight: bold;
          padding: 0 2px;
          border-radius: 2px;
          transition: background-color 0.2s;
        }

        .term-remove-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .btn-clear-all-inline {
          padding: 4px 12px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .btn-clear-all-inline:hover {
          background: #dc2626;
        }

        .search-input-row {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 300px;
          display: flex;
          align-items: center;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          padding: 0;
          overflow: hidden;
        }

        .search-icon-container {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          pointer-events: none;
          color: #9ca3af;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 12px 16px 12px 44px !important;
          font-size: 14px;
          background: transparent;
          color: #374151;
          text-align: left !important;
          width: 100%;
          box-sizing: border-box;
        }

        .search-input::placeholder {
          color: #9ca3af;
          text-align: left !important;
        }

        .search-input:focus {
          outline: none;
        }

        .search-input-wrapper:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .term-type-buttons {
          display: flex;
          gap: 4px;
          margin: 0 8px;
          flex-shrink: 0;
        }

        .term-type-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .include-btn {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .include-btn:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .exclude-btn {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        .exclude-btn:hover:not(:disabled) {
          background: #dc2626;
          border-color: #dc2626;
        }

        .term-type-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #d1d5db;
          cursor: not-allowed;
        }

        .tier-input-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 180px;
          max-width: 200px;
          position: relative;
          flex-shrink: 0;
        }

        .tier-label {
          font-size: 12px;
          font-weight: 500;
          color: #6b7280;
          margin-bottom: 4px;
          white-space: nowrap;
        }

        .tier-dropdown-container {
          position: relative;
          width: 100%;
          min-width: 180px;
        }

        .tier-dropdown-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tier-simple-select {
          flex: 1;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #374151;
          font-weight: 600;
        }

        .tier-simple-select:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .tier-simple-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .info-icon-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: help;
        }

        .info-icon {
          font-size: 16px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .info-icon-container:hover .info-icon {
          opacity: 1;
        }

        .tier-tooltip {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 12px;
          z-index: 1000;
          min-width: 320px;
          max-width: 400px;
        }

        .tooltip-header {
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 8px;
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tooltip-tier {
          padding: 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .tooltip-tier.warning {
          background: #fefce8;
          border-color: #fde047;
        }

        .tooltip-tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .tooltip-tier-label {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .tooltip-tier-score {
          font-size: 12px;
          font-weight: 700;
          color: #059669;
          background: #dcfce7;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-score {
          color: #d97706;
          background: #fef3c7;
        }

        .tooltip-tier-meaning {
          font-size: 11px;
          color: #6b7280;
          line-height: 1.4;
          display: flex;
          align-items: flex-start;
          gap: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-meaning {
          color: #92400e;
        }

        .warning-indicator {
          font-size: 10px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .tier-tooltip::after {
          content: '';
          position: absolute;
          bottom: 100%;
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid white;
        }

        .tier-tooltip::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 1px);
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #d1d5db;
        }

        .search-button-container {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}

.search-button {
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  min-width: 100px;
}

.search-button:hover:not(:disabled) {
  background: #2563eb;
}

.search-button:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .performance-filter-section {
          margin-bottom: 20px;
        }

        .performance-filter-row {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .performance-filter-container,
        .target-container,
        .sort-container,
        .page-size-container {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 120px;
          flex: 1;
        }

        .performance-label-container {
          display: flex;
          align-items: center;
          height: 20px;
        }

        .performance-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
          white-space: nowrap;
        }

        .performance-filter-select,
        .target-value-input,
        .sort-select,
        .page-size-select {
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background: white;
          color: #374151;
          outline: none;
          cursor: pointer;
        }

        .performance-filter-select:focus,
        .target-value-input:focus,
        .sort-select:focus,
        .page-size-select:focus {
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .sort-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .sort-select {
          flex: 1;
        }

        .sort-order-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
          transition: all 0.2s ease;
          user-select: none;
          flex-shrink: 0;
        }

        .sort-order-arrow:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
          color: #111827;
        }
          
        .search-status {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 8px 0;
          font-size: 12px;
          color: #6b7280;
          border-top: 1px solid #e5e7eb;
          margin-top: 8px;
        }

        .search-summary {
          font-weight: 500;
        }

        .search-progress {
          color: #3b82f6;
          font-weight: 500;
        }

        .error-display {
          padding: 12px 16px;
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
          border-radius: 8px;
          margin-top: 16px;
          font-size: 14px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .audience-search-interface {
            padding: 16px;
          }

          .search-input-row {
            flex-direction: column;
            align-items: stretch;
          }

          .search-input-wrapper {
            min-width: auto;
          }

          .tier-input-container {
            width: 100%;
            align-items: stretch;
            min-width: auto;
            max-width: none;
          }

          .tier-dropdown-container {
            min-width: auto;
          }

          .tier-tooltip {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            right: auto;
            bottom: auto;
            width: calc(100vw - 32px);
            max-width: 350px;
            max-height: 60vh;
            overflow-y: auto;
          }

          .tier-tooltip::before,
          .tier-tooltip::after {
            display: none;
          }
        }
      `}</style>
		</div>
	);
};
